<template>
  <v-dialog v-model="dialogForm" persistent width="1000">
    <v-card tile>
      <v-card-title class="headline">{{
        order ? $t("BAFormEditOrder") + order.id : $t("BAFormBook")
      }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="3" class="pb-0">
            <v-menu
              v-model="datepicker"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
              class="ml-5"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :label="$t('BAFormBookDate') + ' *'"
                  type="text"
                  :value="formattedDate"
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  required
                  :error-messages="dateErrors"
                ></v-text-field>
              </template>
              <v-date-picker
                :min="moment.parseZone().format('YYYY-MM-DD')"
                @change="changeFormItems()"
                v-model="selectedDate"
                no-title
                scrollable
                :locale="$i18n.locale"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" class="pb-0">
            <v-select
              :items="location.playgrounds"
              v-model="currentOrder.playground_id"
              dense
              :label="$t('BAFormPlayground') + ' *'"
              item-text="name"
              item-value="id"
              @change="changeFormItems"
              required
              :error-messages="playgroundErrors"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" class="pb-0">
            <v-select
              :items="timeTable"
              v-model="selectedTime"
              dense
              :label="$t('BAFormTime') + ' *'"
              item-text="name"
              item-value="id"
              @change="checkPersonsAndGame"
              required
              :disabled="!currentOrder.playground_id"
              :no-data-text="$t('BAFormNoSessions')"
              :error-messages="timeErrors"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" class="pb-0">
            <v-select
              :items="available_persons"
              v-model="currentOrder.persons"
              dense
              :label="$t('BAFormPersons') + ' *'"
              required
              :disabled="!selectedTime"
              :error-messages="personsErrors"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" class="pb-0">
            <v-select
              :items="order_types"
              v-model="currentOrder.order_type"
              dense
              item-text="title"
              item-value="id"
              @change="selectionOrderType"
              :label="$t('BAFormType') + ' *'"
              required
              :error-messages="orderTypeErrors"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" class="pb-0">
            <v-text-field
              :label="$t('BAFormPromocode')"
              type="text"
              v-model="currentOrder.promocode"
              dense
              :disabled="!selectedTime"
              append-outer-icon="mdi-send"
              @click:append-outer="validatePromocode"
              @change="validatePromocode"
              :error-messages="promocodeErrors"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="3"
            class="pb-0"
            v-if="
              currentOrder.persons &&
              selectedTime &&
              currentOrder.order_type === 1
            "
          >
            <p
              style="font-size: 16px"
              class="mt-2 font-weight-medium"
              v-html="$t('BAFormSum') + convertPrice(computedPrice)"
            ></p>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0">
            <v-text-field
              v-model="currentOrder.name"
              :label="$t('BAFormName') + ' *'"
              type="text"
              dense
              required
              :error-messages="nameErrors"
              @input="$v.currentOrder.name.$touch()"
              @blur="$v.currentOrder.name.$touch()"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0">
            <v-text-field
              v-model="currentOrder.phone"
              :label="$t('BAFormPhone') + ' *'"
              type="text"
              dense
              required
              :error-messages="phoneErrors"
              @input="$v.currentOrder.phone.$touch()"
              @blur="$v.currentOrder.phone.$touch()"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            class="pb-0"
            v-if="localSessionSlot && localSessionSlot.pricing.personal_price"
          >
            <v-select
              :items="games"
              v-model="currentOrder.game_id"
              dense
              item-text="name"
              item-value="id"
              :label="$t('BAFormGame') + ' *'"
              required
              :disabled="
                Boolean(localSessionSlot.orders.length || !selectedTime)
              "
              :error-messages="gameErrors"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0">
            <v-text-field
              v-model="currentOrder.email"
              label="E-mail"
              type="text"
              dense
              :error-messages="emailErrors"
              @blur="$v.currentOrder.email.$touch()"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0" v-if="!this.order">
            <v-text-field
              v-model="currentOrder.seller"
              :label="$t('BAFormSeller')"
              type="text"
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0">
            <v-checkbox
              class="mt-1"
              v-model="currentOrder.bill"
              :label="$t('BAFormBill')"
              :disabled="currentOrder.order_type !== 1"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="4" class="pb-0" v-if="currentOrder.bill">
            <v-text-field
              v-model="currentOrder.cancel_delay"
              :label="$t('BAFormBillActivity')"
              type="number"
              dense
            ></v-text-field>
          </v-col>
          <v-col class="pb-0" cols="12" sm="12">
            <v-textarea
              rows="1"
              v-model="currentOrder.comment"
              :label="$t('BAFormComment')"
              auto-grow
              dense
            ></v-textarea>
          </v-col>
        </v-row>
        <small>* {{ $t("validation") }}</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click="closeForm()">{{
          $t("btnExit")
        }}</v-btn>
        <v-btn
          color="green darken-1"
          text
          @click="submitForm()"
          :disabled="
            $v.currentOrder.$invalid ||
            $v.selectedDate.$dirty ||
            $v.selectedTime.$dirty ||
            disabledBtn
          "
        >
          {{ order ? $t("btnSave") : $t("btnCreate") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, email, helpers, requiredIf } from "vuelidate/lib/validators";
import moment from "moment";

const promoValid = (value) => {
  return value ? true : false;
};
const phoneValid = (value) => {
  return value ? true : false;
};
const phoneRegexp = helpers.regex("phone", /^\+?[0-9]+$/);

export default {
  name: "BookingAdminForm",

  mixins: [validationMixin],

  props: ["order", "sessionSlot", "template", "selectedDay", "dialogForm"],

  data() {
    return {
      datepicker: false,
      disabledBtn: false,
      currentOrder: {
        playground_id: null,
        persons: null,
        order_type: null,
        promocode: "",
        name: "",
        phone: "",
        email: "",
        bill: false,
        cancel_delay: null,
        comment: null,
        session_dt: "",
        playsession: null,
        game_id: null,
        seller: null,
        source: "business.anvio.com",
      },
      localSessionSlot: this.sessionSlot,
      timeTable: [],
      selectedDate: moment().format("YYYY-MM-DD"),
      selectedTime: null,
      available_persons: [],
      maxPlayers: null,
      order_types: [
        {
          id: 1,
          title: this.$i18n.t("BAFormPaid"),
        },
        {
          id: 2,
          title: this.$i18n.t("BAFormTech"),
        },
        {
          id: 3,
          title: this.$i18n.t("BAFormFree"),
        },
      ],
      selectedSession: null,
      discountValid: true,
      phoneValid: true,
      discountValue: false,
      discountType: false,
      firstOpen: true,
      orders: [],
    };
  },

  validations: {
    currentOrder: {
      playground_id: {
        required,
      },
      persons: {
        required,
      },
      order_type: {
        required,
      },
      game_id: {
        required: requiredIf(function () {
          return (
            this.localSessionSlot &&
            this.localSessionSlot.pricing.personal_price
          );
        }),
      },
      name: {
        required,
      },
      phone: {
        required,
        phoneRegexp,
      },
      email: {
        email,
      },
    },
    selectedDate: {
      required,
    },
    selectedTime: {
      required,
    },
    discountValid: {
      promoValid,
    },
    phoneValid: {
      phoneValid,
    },
  },

  computed: {
    ...mapGetters(["location", "BASchedule", "games"]),
    formattedDate() {
      return moment.parseZone(this.selectedDate).format("DD MMMM YYYY");
    },
    completedDate() {
      return (
        moment.parseZone(this.selectedDate).format("YYYY-MM-DD") +
        " " +
        this.selectedTime
      );
    },
    computedPrice() {
      let self = this;
      let price = 0;
      let priceFullBook = 0;
      self.selectedSession = self.BASchedule.find(
        (el) => el.time_start === self.selectedTime
      );
      if (self.selectedSession.pricing.personal_price) {
        if (self.discountType < 1) {
          price =
            self.selectedSession.pricing.personal_price *
            self.currentOrder.persons *
            (1 - self.discountValue * self.discountType);
        } else {
          price =
            self.selectedSession.pricing.personal_price *
              self.currentOrder.persons -
            self.discountValue * self.discountType;
        }
      } else if (self.selectedSession.pricing.session_price) {
        if (self.discountType < 1) {
          price =
            self.selectedSession.pricing.session_price *
            (1 - self.discountValue * self.discountType);
        } else {
          price =
            self.selectedSession.pricing.session_price -
            self.discountValue * self.discountType;
        }
      } else {
        price = 0;
      }

      if (
        self.selectedSession.pricing.personal_price &&
        self.maxPlayers === self.currentOrder.persons &&
        self.selectedSession.pricing.discount > 0
      ) {
        const personalPrice =
          self.selectedSession.pricing.personal_price *
          self.currentOrder.persons;
        priceFullBook =
          personalPrice - personalPrice * self.selectedSession.pricing.discount;
      }

      return priceFullBook === 0
        ? price
        : price > priceFullBook
        ? priceFullBook
        : price;
    },
    dateErrors() {
      const errors = [];
      if (!this.$v.selectedDate.$dirty) return errors;
      !this.$v.selectedDate.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    playgroundErrors() {
      const errors = [];
      if (!this.$v.currentOrder.playground_id.$dirty) return errors;
      !this.$v.currentOrder.playground_id.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    timeErrors() {
      const errors = [];
      if (!this.$v.selectedTime.$dirty) return errors;
      !this.$v.selectedTime.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    personsErrors() {
      const errors = [];
      if (!this.$v.currentOrder.persons.$dirty) return errors;
      !this.$v.currentOrder.persons.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    gameErrors() {
      const errors = [];
      if (!this.$v.currentOrder.game_id.$dirty) return errors;
      !this.$v.currentOrder.game_id.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    orderTypeErrors() {
      const errors = [];
      if (!this.$v.currentOrder.order_type.$dirty) return errors;
      !this.$v.currentOrder.order_type.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    promocodeErrors() {
      const errors = [];
      if (!this.$v.discountValid.$dirty) return errors;
      !this.$v.discountValid.promoValid &&
        errors.push(this.$i18n.t("validationPromocode"));
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.currentOrder.name.$dirty) return errors;
      !this.$v.currentOrder.name.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    phoneErrors() {
      const errors = [];
      if (!this.$v.currentOrder.phone.$dirty) return errors;
      !this.$v.currentOrder.phone.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.currentOrder.phone.phoneRegexp &&
        errors.push(this.$i18n.t("validationPhone"));
      !this.$v.phoneValid.phoneValid &&
        errors.push(this.$i18n.t("validationPhone"));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.currentOrder.email.$dirty) return errors;
      !this.$v.currentOrder.email.email &&
        errors.push(this.$i18n.t("validationEmail"));
      return errors;
    },
  },
  mounted() {
    document.addEventListener("keyup", this.handleKeyup);
  },
  beforeDestroy() {
    document.removeEventListener("keyup", this.handleKeyup);
  },
  methods: {
    ...mapActions([
      "loadBASchedule",
      "loadGamesByPlaygroundId",
      "checkCoupon",
      "createOrder",
      "editOrder",
      "resetState",
    ]),
    convertPrice(value) {
      let currency = this.location.currency;
      let price =
        value > 0
          ? new Intl.NumberFormat(navigator.language.split("-")[0]).format(
              value
            )
          : 0;

      if (currency.is_before) {
        return currency.code + currency.blank_place + price;
      } else {
        return price + currency.blank_place + currency.code;
      }
    },
    changeFormItems() {
      if (!this.firstOpen) {
        this.timeTable = [];
        this.selectedTime = null;
        this.available_persons = [];
        this.currentOrder.persons = null;
        this.currentOrder.promocode = "";
        this.discountValid = true;
        this.discountValue = false;
        this.discountType = false;
      }

      if (this.currentOrder.playground_id && this.selectedDate) {
        this.loadBASchedule({
          playground_id: this.currentOrder.playground_id,
          selected_date: this.selectedDate,
        })
          .then(() => {
            this.maxPlayers = this.location.playgrounds.find(
              (e) => e.id === this.currentOrder.playground_id
            ).max_players;
          })
          .then(() => {
            this.BASchedule.forEach((el, i) => {
              if (
                (el.available_persons > 0 ||
                  el.time_start === this.selectedTime) &&
                !moment.parseZone(el.begin).isBefore(moment.parseZone())
              ) {
                this.timeTable.push(el.time_start);
              }
            });
            if (this.currentOrder.playground_id && this.selectedTime) {
              this.checkPersonsAndGame();
            }
          });
      }
    },
    checkPersonsAndGame() {
      this.available_persons = [];
      let checkedOrder = this.BASchedule.find(
        (el) => el.time_start === this.selectedTime
      );
      this.localSessionSlot = checkedOrder;
      this.currentOrder.game_id = checkedOrder.game
        ? checkedOrder.game.id
        : null;
      if (this.order) {
        let maxPersons;
        if (checkedOrder.pricing.personal_price) {
          if (checkedOrder.time_start === this.sessionSlot.time_start) {
            maxPersons = this.order.persons + checkedOrder.available_persons;
          } else {
            maxPersons = checkedOrder.available_persons;
          }
        } else {
          maxPersons = this.maxPlayers;
        }
        for (let i = 1; i <= maxPersons; i++) {
          this.available_persons.push(i);
        }
      } else {
        const maxPersons = Math.min(
          this.maxPlayers,
          checkedOrder.available_persons
        );
        for (let i = 1; i <= maxPersons; i++) {
          this.available_persons.push(i);
        }
      }
      if (
        this.localSessionSlot.pricing?.personal_price &&
        !checkedOrder.game?.id
      ) {
        this.loadGamesByPlaygroundId(this.currentOrder.playground_id);
      }
    },
    selectionOrderType() {
      if (this.currentOrder.order_type !== 1) {
        this.currentOrder.bill = false;
      }
    },
    validatePromocode() {
      if (!this.currentOrder.promocode) {
        this.discountValid = true;
        this.discountValue = false;
        this.discountType = false;
        return;
      }

      this.checkCoupon({
        code: this.currentOrder.promocode,
        location: this.location.id,
        datetime: this.completedDate,
      }).then((response) => {
        this.discountValid = response.data.valid;
        this.discountType = response.data.type;
        this.discountValue = response.data.discount;
        this.$v.discountValid.$touch();
      });
    },
    clearForm() {
      this.timeTable = [];
      this.selectedTime = null;
      this.available_persons = [];
      this.currentOrder.persons = null;
      this.currentOrder.promocode = "";
      this.discountValid = true;
      this.phoneValid = true;
      this.discountValue = false;
      this.discountType = false;
      this.currentOrder.playground_id = null;
      this.currentOrder.order_type = null;
      this.currentOrder.game_id = null;
      this.currentOrder.name = "";
      this.currentOrder.phone = "";
      this.currentOrder.email = "";
      this.currentOrder.seller = null;
      this.currentOrder.bill = false;
      this.currentOrder.cancel_delay = null;
      this.currentOrder.comment = null;
      this.currentOrder.session_dt = "";
      this.currentOrder.playsession = null;
      this.orders = [];
      this.disabledBtn = false;

      this.$v.$reset();
    },
    submitForm() {
      let self = this;
      self.disabledBtn = true;
      self.currentOrder.session_dt = self.completedDate;
      const order = { ...self.currentOrder };
      if (self.order) {
        self
          .editOrder(self.currentOrder)
          .then(() => {
            self.firstOpen = true;
            self.disabledBtn = false;
            self.$emit("reload-schedule");
            self.$emit("close-dialog");
          })
          .catch((error) => {
            self.phoneValid = !error.response.data.phone;
            self.$v.phoneValid.$touch();
          });
      } else {
        self
          .createOrder(self.currentOrder)
          .then(() => {
            self.firstOpen = true;
            self.disabledBtn = false;
            self.$emit("reload-schedule");
            self.$emit("close-dialog", order);
            self.$emit("visible-alert");
          })
          .catch((error) => {
            self.phoneValid = !error.response.data.phone;
            self.$v.phoneValid.$touch();
          });
      }
    },
    closeForm() {
      this.$emit("close-form");
      this.firstOpen = true;
    },
    handleKeyup(event) {
      if (event.key === "Escape" || event.keyCode === 27) {
        this.$emit("close-form");
        this.firstOpen = true;
      }
    },
  },
  watch: {
    dialogForm(visible) {
      if (visible) {
        if (this.sessionSlot) {
          this.orders = this.sessionSlot.orders;
          this.selectedDate = moment(this.selectedDay).format("YYYY-MM-DD");
          this.selectedTime = this.sessionSlot.time_start;
          this.currentOrder.playsession = this.sessionSlot.session_id;
          this.currentOrder.playground_id = this.sessionSlot.playground;
          if (this.order) {
            this.currentOrder.id = this.order.id;
            this.currentOrder.persons = this.order.persons;
            this.currentOrder.order_type = this.order.order_type;
            this.currentOrder.game_id = this.order.game
              ? this.order.game.id
              : null;
            this.currentOrder.promocode = this.order.promocode;
            this.currentOrder.name = this.order.name;
            this.currentOrder.phone = this.order.phone;
            this.currentOrder.email = this.order.email;
            this.currentOrder.bill = this.order.bill;
            this.currentOrder.comment = "";
          }
          this.changeFormItems();
        }
        if (!this.order) {
          this.currentOrder.order_type = this.order_types[0].id;
        }
        this.firstOpen = false;
        this.resetState();
      } else {
        this.clearForm();
      }
    },
    selectedTime() {
      if (!this.order) {
        if (this.available_persons.length === 1) {
          this.currentOrder.persons = 1;
        } else {
          this.currentOrder.persons = null;
        }
      }
    },
    "currentOrder.seller": function (value) {
      if (value === "") {
        this.currentOrder.seller = null;
      }
    },
  },
};
</script>
